import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  RefreshCw, Loader2, ShieldCheck, ShieldAlert, AlertTriangle,
  Copy, Unlink, Database, ChevronDown, ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_ICON = {
  Duplicates: Copy,
  Orphans: Unlink,
  'Missing Required Fields': AlertTriangle,
  'Collection Sizes': Database,
};

const STATUS_META = {
  healthy: { label: 'Healthy', color: 'emerald', Icon: ShieldCheck },
  warning: { label: 'Warning', color: 'amber', Icon: ShieldAlert },
  critical: { label: 'Critical', color: 'red', Icon: ShieldAlert },
};

const DataHealth = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/data-health`, { headers: getAuthHeaders() });
      setReport(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Data Health report load nahi ho saka');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-300">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Scanning database…
      </div>
    );
  }
  if (!report) return null;

  const status = STATUS_META[report.summary?.status || 'healthy'];

  return (
    <div className="space-y-6" data-testid="admin-data-health-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Data Health Dashboard</h2>
          <p className="text-slate-400 text-sm mt-1">
            Live scan of the database for duplicates, orphans &amp; missing fields.
          </p>
        </div>
        <Button
          onClick={fetchReport}
          disabled={loading}
          variant="outline"
          className="gap-2 bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
          data-testid="data-health-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Re-scan
        </Button>
      </div>

      {/* Summary Card */}
      <div className={`rounded-2xl border p-6 bg-gradient-to-br from-${status.color}-900/40 to-slate-900 border-${status.color}-700/40`}>
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-full bg-${status.color}-500/20 flex items-center justify-center`}>
            <status.Icon className={`w-7 h-7 text-${status.color}-300`} />
          </div>
          <div className="flex-1">
            <div className={`text-${status.color}-300 text-sm uppercase tracking-wide font-medium`}>
              Overall Status
            </div>
            <div className="text-3xl font-bold text-white mt-1">
              {status.label}
              <span className="text-base font-medium text-slate-400 ml-3">
                ({report.summary?.total_issues || 0} issue{report.summary?.total_issues === 1 ? '' : 's'} detected)
              </span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <SummaryStat label="Duplicate Groups" value={report.summary?.duplicate_groups || 0} color="rose" />
          <SummaryStat label="Orphan Records" value={report.summary?.orphan_records || 0} color="amber" />
          <SummaryStat label="Missing Fields" value={report.summary?.missing_fields || 0} color="cyan" />
        </div>
      </div>

      {/* Sections */}
      {report.sections?.map((section, sIdx) => {
        const Icon = SECTION_ICON[section.title] || Database;
        return (
          <div key={sIdx} className="rounded-2xl border border-slate-700 bg-slate-900/60 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-3">
              <Icon className="w-5 h-5 text-purple-300" />
              <h3 className="text-lg font-semibold text-white">{section.title}</h3>
            </div>
            <div className="divide-y divide-slate-800">
              {section.items.length === 0 && (
                <div className="px-6 py-4 text-sm text-slate-500">No items to display.</div>
              )}
              {section.items.map((item, iIdx) => {
                const key = `${sIdx}-${iIdx}`;
                const isExpandable = Array.isArray(item.rows) && item.rows.length > 0;
                const issueCount = item.count ?? 0;
                const isClean = issueCount === 0 && section.title !== 'Collection Sizes';
                const isSize = section.title === 'Collection Sizes';
                return (
                  <div key={key} className="px-6 py-3">
                    <button
                      type="button"
                      onClick={() => isExpandable && toggle(key)}
                      className={`flex items-center justify-between w-full text-left ${isExpandable ? 'cursor-pointer hover:bg-slate-800/40 -mx-2 px-2 py-1 rounded' : 'cursor-default'}`}
                      data-testid={`data-health-item-${section.title.toLowerCase().replace(/ /g, '-')}-${iIdx}`}
                    >
                      <div className="flex items-center gap-2">
                        {isExpandable && (
                          expanded[key] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />
                        )}
                        <span className="text-slate-200 text-sm">
                          {item.label || item.collection}
                        </span>
                        {item.collection && item.label && (
                          <code className="text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">
                            {item.collection}
                          </code>
                        )}
                      </div>
                      <span
                        className={`text-sm font-semibold px-2.5 py-0.5 rounded-full ${
                          isSize
                            ? 'bg-slate-700 text-slate-200'
                            : isClean
                            ? 'bg-emerald-900/40 text-emerald-300'
                            : 'bg-rose-900/40 text-rose-300'
                        }`}
                      >
                        {issueCount}
                      </span>
                    </button>
                    {isExpandable && expanded[key] && (
                      <div className="mt-3 ml-6 rounded-lg bg-slate-950/60 border border-slate-800 p-3 overflow-x-auto">
                        <pre className="text-xs text-slate-300 whitespace-pre-wrap break-words">
                          {JSON.stringify(item.rows, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="text-xs text-slate-500 text-right">
        Generated at: {report.generated_at ? new Date(report.generated_at).toLocaleString() : '—'}
      </div>
    </div>
  );
};

const SummaryStat = ({ label, value, color }) => (
  <div className={`rounded-xl bg-${color}-500/10 border border-${color}-500/30 px-4 py-3`}>
    <div className={`text-xs text-${color}-300 uppercase tracking-wide`}>{label}</div>
    <div className="text-2xl font-bold text-white mt-1">{value}</div>
  </div>
);

export default DataHealth;
